import React, { useState, useRef, useEffect } from 'react';
import {
    useGetAllVehicleWarrantyPeriodQuery,
    useUpdateVehicleWarrantyPeriodMutation,
    useLazyGetVehicleWarrantyPeriodByIdQuery,
    useLazyGetVehicleWarrantyPeriodDocumentsByIdQuery,
    useUploadVehicleWarrantyPeriodDocumentMutation,
    useDeleteDocumentVehicleWarrantyPeriodMutation
} from '../../../../../api/endpoints/vehicleWarrantyPeriod';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../../api/endpoints/documentContent';
import toastService from '../../../../../utils/toastService';
import DocumentDownloadService from '../../../../../utils/documentDownloadService';

const useVehicleWarrantyPeriodData = () => {
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 1
    });

    // Sayfa değişiklikleri için ayrı loading state
    const [isPageChanging, setIsPageChanging] = useState(false);

    const [activeFilters, setActiveFilters] = useState({
        carItemCode: '',
        chassisNo: ''
    });

    const toastRef = useRef(null);
    const [documentDownloadService] = useState(() => new DocumentDownloadService(toastRef));

    const {
        data: apiVehicleWarrantyPeriod,
        isLoading,
        isFetching,
        refetch
    } = useGetAllVehicleWarrantyPeriodQuery({
        pageNumber: lazyParams.page,
        pageSize: lazyParams.rows,
        carItemCode: activeFilters.carItemCode || undefined,
        chassisNo : activeFilters.chassisNo || undefined
    });

    const [triggerSelectedItemFetch, { data: selectedItemResponse, isLoading: isLoadingSelectedItem, error: selectedItemError, reset: resetSelectedItem }] = useLazyGetVehicleWarrantyPeriodByIdQuery();

    const [getDocuments, { data: documentsResponse, isLoading: isLoadingDocuments, error: documentsError }] = useLazyGetVehicleWarrantyPeriodDocumentsByIdQuery();

    const [triggerDownloadByKey, { isLoading: isDownloadingDocument }] = useLazyDownloadDocumentByKeyQuery();

    const [updateVehicleWarrantyPeriod] = useUpdateVehicleWarrantyPeriodMutation();
    const [uploadDocument] = useUploadVehicleWarrantyPeriodDocumentMutation();
    const [deleteDocument] = useDeleteDocumentVehicleWarrantyPeriodMutation();

    const handleFilterChange = (updatedFilters) => {
        const apiFilters = {
            carItemCode: updatedFilters.carItemCode || '',
            chassisNo : updatedFilters.chassisNo || ''
        };

        setActiveFilters(apiFilters);

        setLazyParams((prevParams) => ({
            ...prevParams,
            first: 0,
            page: 1
        }));
    };

    const initFilters = () => {
        setActiveFilters({
            carItemCode: '',
            chassisNo:''
        });
        setLazyParams((prevParams) => ({
            ...prevParams,
            first: 0,
            page: 1
        }));
    };

    // Function to create clean payload for update
    const createUpdatePayload = (formData) => {
        // Only include fields that should be sent in the update request
        const updateFields = {
            id: formData.id,
            addWarrantyPeriod: formData.addWarrantyPeriod,
            addWarrantyKmInfo: formData.addWarrantyKmInfo,
            curWarrantyPeriod: formData.curWarrantyPeriod,
            curWarrantyKmInfo: formData.curWarrantyKmInfo,
            odometerKmInfo: formData.odometerKmInfo,
            addWarrDetails: formData.addWarrDetails,
            dealerId: formData.dealerId,
            endUser: formData.endUser,
            sellDate: formData.sellDate,
            bid: formData.bid,
            bidWarrantyPeriod: formData.bidWarrantyPeriod,
            bidWarrantyKmInfo: formData.bidWarrantyKmInfo,
            // Add these fields from the original data if they exist
            carItemCode: formData.carItemCode,
            chassisNo: formData.chassisNo,
            modelYear: formData.modelYear,
            startDate: formData.startDate,
            endDate: formData.endDate,
            returnCount: formData.returnCount
        };

        // Remove undefined/null values that shouldn't be sent
        const cleanPayload = {};
        Object.keys(updateFields).forEach(key => {
            const value = updateFields[key];
            if (value !== undefined && value !== null) {
                cleanPayload[key] = value;
            }
        });

        return cleanPayload;
    };

    const saveVehicleWarrantyPeriod = async (data, hasFormChanged) => {
        try {
            // Form değişikliği kontrolü
            if (!hasFormChanged && (!data.files || data.files.length === 0)) {
                toastService.warn('Güncelleme için herhangi bir değişiklik yapılmadı');
                return false;
            }

            // Form verilerini güncelle
            if (hasFormChanged) {
                // Create clean payload with only necessary fields
                const updatePayload = createUpdatePayload(data);

                console.log('Update payload:', updatePayload); // Debug log

                await updateVehicleWarrantyPeriod(updatePayload).unwrap();
            }

            // Dosya yükleme işlemi
            if (data.files && data.files.length > 0 && data.id) {
                // Mevcut dosyalar varsa sil (sadece bir dosya olacağı için)
                if (documentsResponse?.data && documentsResponse.data.length > 0) {
                    for (const doc of documentsResponse.data) {
                        await deleteDocument({
                            id: data.id,
                            documentId: doc.id
                        }).unwrap();
                    }
                }

                // Yeni dosyayı yükle
                for (const file of data.files) {
                    await uploadDocument({
                        vehicleAdditionalWarrantyPeriodId: data.id,
                        file: file,
                        description: file.name || 'Uploaded document'
                    }).unwrap();
                }
            }

            toastService.success('Araç garanti süresi başarıyla güncellendi');
            refetch();
            if (selectedItemId) {
                getDocuments(selectedItemId, true);
            }
            return true;
        } catch (error) {
            console.error('Error updating vehicle warranty period:', error);
            toastService.error('Araç garanti süresi güncellenirken hata oluştu');
            return false;
        }
    };

    const selectItem = (id) => {
        setSelectedItemId(id);
        if (id) {
            triggerSelectedItemFetch(id);
            getDocuments(id);
        }
    };

    const clearSelection = () => {
        setSelectedItemId(null);
        resetSelectedItem();
    };

    const onPage = (event) => {
        // Sayfa değişikliği başladığında loading'i aktif et
        setIsPageChanging(true);

        setLazyParams((prevState) => ({
            ...prevState,
            page: event.page + 1,
            first: event.first,
            rows: event.rows
        }));

        // Loading'i biraz gecikmeli olarak kapat (API çağrısının tamamlanması için)
        setTimeout(() => {
            setIsPageChanging(false);
        }, 100);
    };

    const handleDocumentDownload = async (documentData) => {
        try {
            if (documentDownloadService.toastRef !== toastRef) {
                documentDownloadService.toastRef = toastRef;
            }

            await documentDownloadService.downloadByKey(triggerDownloadByKey, documentData.fileKey, documentData);
        } catch (error) {
            // Fallback toast notification if toastRef is not available
            if (toastRef.current?.show) {
                toastRef.current.show({
                    severity: 'error',
                    summary: 'İndirme Hatası',
                    detail: 'Dosya indirilirken hata oluştu',
                    life: 3000
                });
            }
        }
    };

    // Function to set the toast ref from the component
    const setToastRef = (ref) => {
        toastRef.current = ref;
        if (documentDownloadService) {
            documentDownloadService.toastRef = toastRef;
        }
    };

    // API çağrısı tamamlandığında page changing durumunu kapat
    useEffect(() => {
        if (!isFetching && isPageChanging) {
            setIsPageChanging(false);
        }
    }, [isFetching, isPageChanging]);

    return {
        apiVehicleWarrantyPeriod: apiVehicleWarrantyPeriod?.data || { items: [], totalCount: 0 },
        isLoading,
        isFetching: isFetching || isPageChanging, // Sayfa değişikliği durumunu da dahil et
        lazyParams,
        activeFilters,
        handleFilterChange,
        initFilters,

        selectedItem: selectedItemResponse?.data || null,
        isLoadingSelectedItem,
        selectedItemError,

        documents: documentsResponse?.data || [],
        isLoadingDocuments,
        documentsError,

        saveVehicleWarrantyPeriod,
        selectItem,
        clearSelection,
        onPage,

        handleDocumentDownload,
        isDownloadingDocument,
        toastRef,
        setToastRef
    };
};

export default useVehicleWarrantyPeriodData;